/* eslint-disable react/jsx-max-depth */
import classnames from 'classnames';
import { capitalize } from 'lodash';
import { useMemo } from 'react';
import { OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ErrorBoundary } from '@sentry/react';
import Icon from '../../../components/icon/Icon';
import Activity from '../../../components/timeline/Activity';
import ErrorFallback from '@/components/ErrorFallback';
import type { ActivityMetadata } from '@larki/shared/types/activity.type';
import text from '../../../text';
import {
  isActivityFileDownload,
  isActivityType,
} from '../../../utilities/activity';
import { formatDate, formatTime } from '../../../utilities/date';
import { isStaff } from '../../../utilities/user';
import { ACTIVITY_TYPES } from '../../../constants/activity';
import { useFileManagerContext } from '../../../hooks/useFileManagerContext';
import { useToggleActivityIsHiddenMutation } from '../../../redux/rtk';
import useUser from '../../../mp/hooks/useUser';
import ActivityAsset from '../ActivityAsset';
import JobStatusUpdate from '../JobStatusUpdate';
import ActivityStaffPriceEditProvider from './StaffPriceEditProvider';
import ActivityTemplate from './Template';
import DropdownMenu from './DropdownMenu';
import { useQuotePdfProps } from './useQuotePdfProps';
import { useJobActivityActions } from './useActions';
import { useActivityMenuVisibility } from './useMenuVisibility';
import { TypeIconMap } from './utils';

// The activity and project models carry many fields not declared in the
// shared schemas; keep the entry-point loose and let downstream hooks narrow.
type JobActivityProps = {
  jobActivity: Record<string, any> & {
    metadata?: ActivityMetadata | null;
  };
  currentProject: Record<string, any>;
};

// TODO: Refactor this to project activity
const JobActivity = (props: JobActivityProps) => {
  const {
    jobActivity: {
      id,
      created_at,
      created_assets,
      title,
      description,
      type,
      job_id,
      project_id,
      detailed_status_update,
      is_notification_sent,
      visibility,
      created_user,
      metadata,
      superseded_assets,
      is_hidden,
    },
    currentProject,
  } = props;

  const currentUser = useUser();
  const { scrollContainerRef } = useFileManagerContext();

  const job = useMemo(
    () => currentProject.jobs.find((j) => j.id === job_id),
    [currentProject.jobs, job_id]
  );

  const quotePdfProps = useQuotePdfProps({ metadata, job, currentProject });

  const menuVisibility = useActivityMenuVisibility({
    currentUser,
    metadata,
    job,
    type,
    createdAssets: created_assets,
    supersededAssets: superseded_assets,
    createdUser: created_user,
    projectId: project_id,
  });

  const actions = useJobActivityActions({
    activityId: id,
    projectId: project_id,
    type,
    isSuperseded: menuVisibility.isSuperseded,
  });

  const [toggleActivityIsHidden] = useToggleActivityIsHiddenMutation();
  const onToggleHidden = () =>
    toggleActivityIsHidden({
      projectId: project_id,
      activityId: id,
      isHidden: !is_hidden,
    });

  const getActivityIcon = () => {
    // handle old File Downloaded activities w/c are still under assets
    if (isActivityFileDownload({ title, description }) && type !== 'larki') {
      return TypeIconMap.alert;
    }
    return TypeIconMap[type as keyof typeof TypeIconMap] || 'speech';
  };

  const getActivityType = (t: string) => {
    // handle old File Downloaded activites that were saved as notes
    if (
      [ACTIVITY_TYPES.note, ACTIVITY_TYPES.alert].includes(t) &&
      isActivityFileDownload({ title, description })
    ) {
      return 'download';
    }
    return t;
  };

  const { jobActivity } = props;

  if (isActivityType(jobActivity, 'status')) {
    return (
      <JobStatusUpdate
        detailedStatuses={detailed_status_update}
        createdDate={created_at}
        activityTitle={title}
        activityDescription={description}
        jobId={job_id}
        projectId={project_id}
        isHidden={is_hidden}
        menu={
          <DropdownMenu
            scrollContainerRef={scrollContainerRef}
            quotePdfProps={null}
            visibility={menuVisibility}
            currentUserIsStaff={!!currentUser.isStaff}
            projectId={project_id}
            isHidden={is_hidden}
            onSupersede={actions.onSupersede}
            onDeleteActivity={actions.onDeleteActivity}
            onToggleHidden={onToggleHidden}
          />
        }
      />
    );
  }

  return (
    <ActivityStaffPriceEditProvider
      enabled={menuVisibility.isEditPriceVisible}
      jobId={job?.id ?? null}
      asMember={menuVisibility.creatorIsEssentialUser}
      requestForQuoteId={
        metadata?.variant === 'request_for_quote'
          ? metadata.request_for_quote_id
          : undefined
      }
    >
      <Activity
        className={classnames('JobActivity', {
          isStaff: isStaff(visibility),
        })}
      >
        <>
          <Activity.Icon iconProps={{ icon: getActivityIcon() }} />
          <Activity.Card
            className={classnames({
              superseded: menuVisibility.isSuperseded,
              isHidden: is_hidden,
            })}
          >
            {/* @ts-expect-error Activity.Card.Header is not typed */}
            <Activity.Card.Header>
              <Row className='m-0 w-100'>
                <div className='main-content d-flex flex-fill font-weight--500 font-12 align-items-center'>
                  <Activity.Card.Type
                    type={type ? capitalize(getActivityType(type)) : null}
                  />
                  <Activity.Card.Date
                    hasPipeSeparator={job_id || project_id}
                    date={formatDate(created_at)}
                    time={formatTime(created_at, 'h:mmaaa')}
                  />

                  {project_id ? (
                    <Activity.Card.HeaderElement
                      className={classnames(
                        { hasPipeSeparator: !!job_id },
                        'projectId'
                      )}
                    >
                      {text('projectId')}:{project_id}
                    </Activity.Card.HeaderElement>
                  ) : null}

                  {job_id ? (
                    <Activity.Card.HeaderElement className='jobId'>
                      {text('orderId')}:{job_id}
                    </Activity.Card.HeaderElement>
                  ) : null}

                  {is_notification_sent ? (
                    <OverlayTrigger
                      delay={{ show: 250, hide: 250 }}
                      placement='top'
                      overlay={
                        <Tooltip id='notif-sent' className='notification-sent'>
                          {text('notificationSent')}
                        </Tooltip>
                      }
                    >
                      <Icon
                        icon='envelope'
                        className='ml-3 pt-1'
                        style={{ width: 20 }}
                      />
                    </OverlayTrigger>
                  ) : null}
                </div>
                {isStaff(visibility) && (
                  <span className='larki-tag'>{`[${text('larkiOnly')}]`} </span>
                )}
                <Activity.Card.HeaderRight classNames='d-flex flex-row align-items-center justify-content-end flex-fill'>
                  <span className='mr-1'>
                    {menuVisibility.isSuperseded ? text('supersededStatus') : null}
                  </span>
                  {(!created_user?.full_name || isStaff(created_user?.role)) &&
                  !isStaff(currentUser.profile?.role)
                    ? 'LARKI'
                    : created_user?.full_name}

                  <DropdownMenu
                    scrollContainerRef={scrollContainerRef}
                    quotePdfProps={quotePdfProps}
                    visibility={menuVisibility}
                    currentUserIsStaff={!!currentUser.isStaff}
                    projectId={project_id}
                    isHidden={is_hidden}
                    onSupersede={actions.onSupersede}
                    onDeleteActivity={actions.onDeleteActivity}
                    onToggleHidden={onToggleHidden}
                  />
                </Activity.Card.HeaderRight>
              </Row>
              <Row className='w-100 d-flex font-weight--400 font-12'>
                {job?.full_address}
              </Row>
            </Activity.Card.Header>
            <div className='ActivityContent'>
              {menuVisibility.activityMetadata ? (
                <ErrorBoundary fallback={ErrorFallback}>
                  <ActivityTemplate
                    metadata={menuVisibility.activityMetadata}
                    job={job}
                    createdUser={created_user}
                  />
                </ErrorBoundary>
              ) : (
                <>
                  <div className='ActivityTitle font-16 font-weight--600 text-uppercase'>
                    {/* Relabel the legacy 'Files to download' title only. */}
                    {title === 'Files to download' ? text('files') : title}
                  </div>
                  {created_assets ? (
                    <ActivityAsset
                      onDelete={actions.onDeleteAsset}
                      projectId={project_id}
                      assets={created_assets}
                      currentUser={currentUser.profile}
                      isSuperseded={menuVisibility.isSuperseded}
                      isPaymentFlowUpload={
                        menuVisibility.isUploadOriginFromPaymentFlow
                      }
                    />
                  ) : null}
                  {menuVisibility.isUploadOriginFromPaymentFlow ? null : (
                    <ReactMarkdown
                      linkTarget='_blank'
                      remarkPlugins={[remarkGfm]}
                    >
                      {description}
                    </ReactMarkdown>
                  )}
                </>
              )}
            </div>
          </Activity.Card>
        </>
      </Activity>
    </ActivityStaffPriceEditProvider>
  );
};

export default JobActivity;
